import type { ElementType } from 'react';

import { InfoIcon as PhosphorInfoIcon } from '@phosphor-icons/react/ssr';
import { ObjectUtils } from '@polyseme/utils/typescript/typescript-utils';
import {
  Archive,
  ArrowDown,
  ArrowDownRight,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BadgeEuro,
  Banknote,
  BarChart,
  BarChartBig,
  LineChart,
  Bell,
  Blend,
  BookOpen,
  Brain,
  Brush,
  Bug,
  Building,
  Building2,
  Cake,
  CalendarClock,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  CircleAlert,
  CircleCheck,
  CircleCheckBig,
  CircleDot,
  CircleFadingPlus,
  CircleHelp,
  CirclePlay,
  CirclePlus,
  CircleUser,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  CloudCog,
  Cog,
  Coins,
  Compass,
  ContactRound,
  CornerRightDown,
  Delete,
  Dot,
  Download,
  Ellipsis,
  Equal,
  Euro,
  Eye,
  EyeOff,
  Facebook,
  File,
  FileBadge,
  FileCheck2,
  Files,
  FileText,
  Filter,
  Gem,
  Gift,
  Goal,
  GraduationCap,
  Grip,
  Hand,
  HandCoins,
  Hourglass,
  House,
  Import,
  Info,
  Instagram,
  Landmark,
  Laptop,
  Leaf,
  Lightbulb,
  Link,
  Linkedin,
  List,
  LoaderCircle,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Minus,
  Moon,
  Network,
  Paintbrush,
  PartyPopper,
  Pencil,
  PenTool,
  Percent,
  PersonStanding,
  Phone,
  PhoneCall,
  PiggyBank,
  Play,
  Plus,
  Power,
  ReceiptEuro,
  Rocket,
  RotateCw,
  Save,
  Scale,
  ScrollText,
  Search,
  ServerCog,
  ShieldBan,
  ShoppingCart,
  Sparkles,
  SquareCheckBig,
  SquarePlus,
  SquareX,
  Star,
  Sun,
  Table2,
  Target,
  TestTubeDiagonal,
  Ticket,
  Timer,
  Trash2,
  TrendingUp,
  TriangleAlert,
  User,
  UserCog,
  UserRound,
  UserPlus,
  UserRoundPen,
  Users2,
  Videotape,
  Wallet,
  WandSparkles,
  X,
  Youtube,
  Divide,
  PanelTopDashed,
  LockKeyhole,
  MessageCircleQuestion,
  ExternalLink,
  CircleDashed,
  ArchiveRestore,
  MailOpen,
  TrendingDown,
  ArrowLeftRight,
} from 'lucide-react';

const PHOSPHOR_ICON_MAP = {
  info: PhosphorInfoIcon,
} as const;

const ICON_MAP = {
  ...Object.entries(PHOSPHOR_ICON_MAP).reduce(
    (acc, [key, IconComponent]) => ({
      ...acc,
      [`phosphor-${key}`]: IconComponent,
    }),
    {} as Record<`phosphor-${keyof typeof PHOSPHOR_ICON_MAP}`, ElementType>
  ),
  'archive-restore': ArchiveRestore,
  'arrow-left-right': ArrowLeftRight,
  'arrow-down-right': ArrowDownRight,
  'arrow-down-to-line': ArrowDownToLine,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up-right': ArrowUpRight,
  'badge-euro': BadgeEuro,
  banknote: Banknote,
  'bar-chart-big': BarChartBig,
  'bar-chart': BarChart,
  'line-chart': LineChart,
  brush: Brush,
  'building-2': Building2,
  'calendar-clock': CalendarClock,
  'calendar-days': CalendarDays,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'circle-dot': CircleDot,
  'circle-dashed': CircleDashed,
  'chevron-up': ChevronUp,
  'chevrons-up-down': ChevronsUpDown,
  'circle-alert': CircleAlert,
  'circle-check-big': CircleCheckBig,
  'circle-check': CircleCheck,
  'circle-help': CircleHelp,
  'circle-play': CirclePlay,
  'circle-plus': CirclePlus,
  'circle-user': CircleUser,
  'circle-x': CircleX,
  'circle-fading-plus': CircleFadingPlus,
  'corner-right-down': CornerRightDown,
  'clipboard-check': ClipboardCheck,
  'clipboard-list': ClipboardList,
  'cloud-cog': CloudCog,
  'contact-round': ContactRound,
  'eye-off': EyeOff,
  'file-badge': FileBadge,
  'file-check-2': FileCheck2,
  'file-text': FileText,
  files: Files,
  'hand-coins': HandCoins,
  lightbulb: Lightbulb,
  'loader-circle': LoaderCircle,
  'log-out': LogOut,
  lock: Lock,
  'lock-keyhole': LockKeyhole,
  'message-circle': MessageCircle,
  'message-circle-question': MessageCircleQuestion,
  'party-popper': PartyPopper,
  'person-standing': PersonStanding,
  'piggy-bank': PiggyBank,
  'scroll-text': ScrollText,
  'shopping-cart': ShoppingCart,
  'square-check-big': SquareCheckBig,
  'square-plus': SquarePlus,
  'square-x': SquareX,
  'table-2': Table2,
  'test-tube-diagonal': TestTubeDiagonal,
  'trash-2': Trash2,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  'triangle-alert': TriangleAlert,
  'user-cog': UserCog,
  'user-round': UserRound,
  'user-round-pen': UserRoundPen,
  'wand-sparkles': WandSparkles,
  archive: Archive,
  bell: Bell,
  blend: Blend,
  brain: Brain,
  bookOpen: BookOpen,
  bug: Bug,
  building: Building,
  cake: Cake,
  check: Check,
  cog: Cog,
  coins: Coins,
  compass: Compass,
  delete: Delete,
  dot: Dot,
  download: Download,
  divide: Divide,
  ellipsis: Ellipsis,
  equal: Equal,
  euro: Euro,
  'external-link': ExternalLink,
  eye: Eye,
  facebook: Facebook,
  file: File,
  filter: Filter,
  gem: Gem,
  gift: Gift,
  goal: Goal,
  graduationCap: GraduationCap,
  grip: Grip,
  hand: Hand,
  hourglass: Hourglass,
  house: House,
  import: Import,
  info: Info,
  instagram: Instagram,
  landmark: Landmark,
  laptop: Laptop,
  leaf: Leaf,
  link: Link,
  linkedin: Linkedin,
  list: List,
  locate: MapPin,
  mail: Mail,
  'mail-open': MailOpen,
  menu: Menu,
  minus: Minus,
  moon: Moon,
  network: Network,
  pencil: Pencil,
  'pen-tool': PenTool,
  percent: Percent,
  phone: Phone,
  'phone-call': PhoneCall,
  paintbrush: Paintbrush,
  'panel-top-dashed': PanelTopDashed,
  play: Play,
  plus: Plus,
  power: Power,
  'receipt-euro': ReceiptEuro,
  refresh: RotateCw,
  rocket: Rocket,
  'server-cog': ServerCog,
  save: Save,
  scale: Scale,
  search: Search,
  shieldban: ShieldBan,
  sparkles: Sparkles,
  star: Star,
  sun: Sun,
  target: Target,
  ticket: Ticket,
  timer: Timer,
  user: User,
  'user-plus': UserPlus,
  users: Users2,
  videotape: Videotape,
  wallet: Wallet,
  x: X,
  youtube: Youtube,
} as const;

export const iconNames = ObjectUtils.keys(ICON_MAP);
export type IconName = keyof typeof ICON_MAP;

export const ICONS: Record<IconName, ElementType> = ICON_MAP;
